import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { auth } from "../firebase";
import logo from "../assets/river-ranch-logo.png";
import hero from "../assets/hero-poker.jpg";

const COPPER = "#B87333";

function FeaturePill({ children }) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold tracking-wide"
      style={{
        borderColor: "rgba(184,115,51,0.28)",
        background: "rgba(184,115,51,0.10)",
        color: "rgba(255,255,255,0.92)",
      }}
    >
      {children}
    </span>
  );
}

function InfoCard({ title, text }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/75 backdrop-blur p-5">
      <h3 className="text-lg font-bold" style={{ color: COPPER }}>
        {title}
      </h3>
      <p className="mt-2 text-zinc-300 text-sm sm:text-base leading-relaxed">
        {text}
      </p>
    </div>
  );
}

function StepCard({ step, title, text }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/75 backdrop-blur p-5">
      <div
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold"
        style={{
          backgroundColor: "rgba(184,115,51,0.14)",
          color: COPPER,
          border: "1px solid rgba(184,115,51,0.25)",
        }}
      >
        {step}
      </div>

      <h3 className="mt-4 text-lg font-bold text-zinc-100">{title}</h3>
      <p className="mt-2 text-zinc-400 text-sm sm:text-base leading-relaxed">
        {text}
      </p>
    </div>
  );
}

export default function Home() {
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setIsAuthed(!!u));
    return () => unsub();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background */}
      <img
        src={hero}
        alt="Poker table"
        className="absolute inset-0 w-full h-full object-cover opacity-35"
      />
      <div className="absolute inset-0 bg-black/75" />

      {/* Copper glow */}
      <div className="absolute inset-0 opacity-35 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-3xl bg-[#B87333]/20" />
        <div className="absolute -bottom-52 -right-52 h-[620px] w-[620px] rounded-full blur-3xl bg-[#B87333]/15" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8 sm:py-10 flex flex-col min-h-screen">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={logo}
              alt="River Ranch Poker Club"
              className="h-12 w-auto shrink-0"
            />
            <div className="hidden sm:block min-w-0">
              <p className="font-semibold tracking-wide text-zinc-100">
                River Ranch Poker Club
              </p>
              <p className="text-zinc-400 text-sm">Private Members Club</p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {isAuthed ? (
              <Link
                to="/dashboard"
                className="px-4 py-2 rounded bg-zinc-900/70 backdrop-blur border border-zinc-800 hover:border-zinc-700 text-zinc-100 font-semibold"
              >
                Go to Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 rounded bg-zinc-900/70 backdrop-blur border border-zinc-800 hover:border-zinc-700 text-zinc-100 font-semibold"
              >
                Member Login
              </Link>
            )}

            <Link
              to="/apply"
              className="px-4 py-2 rounded font-semibold text-zinc-950"
              style={{ backgroundColor: COPPER }}
            >
              Apply
            </Link>
          </div>
        </div>

        {/* Hero */}
        <section className="pt-10 sm:pt-14 lg:pt-16">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
            <div>
              <div className="flex flex-wrap gap-2 mb-5">
                <FeaturePill>Private Members Club</FeaturePill>
                <FeaturePill>Tournaments</FeaturePill>
                <FeaturePill>Card Games</FeaturePill>
                <FeaturePill>Fayette County, Texas</FeaturePill>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-4xl">
                Poker nights with an
                <span className="block" style={{ color: COPPER }}>
                  upscale atmosphere.
                </span>
              </h1>

              <p className="mt-5 text-zinc-300 text-lg leading-relaxed max-w-2xl">
                River Ranch Poker Club is a private members club for 
                card games and club events in Fayette County, Texas.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/apply"
                  className="px-5 py-3 rounded font-semibold text-zinc-950"
                  style={{ backgroundColor: COPPER }}
                >
                  Apply for Membership
                </Link>

                {isAuthed ? (
                  <Link
                    to="/dashboard"
                    className="px-5 py-3 rounded font-semibold bg-zinc-900/70 backdrop-blur border border-zinc-800 hover:border-zinc-700 text-zinc-100"
                  >
                    Enter Member Portal
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="px-5 py-3 rounded font-semibold bg-zinc-900/70 backdrop-blur border border-zinc-800 hover:border-zinc-700 text-zinc-100"
                  >
                    Member Login
                  </Link>
                )}
              </div>
            </div>

            {/* Right hero card */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950/75 backdrop-blur p-6 lg:p-7">
              <p className="text-zinc-400 text-sm">River Ranch Poker Club</p>
              <h2 className="mt-2 text-2xl font-bold" style={{ color: COPPER }}>
                Built for a better game night.
              </h2>

              <div className="mt-5 space-y-3 text-zinc-200">
                <div className="flex items-start gap-3">
                  <div
                    className="mt-2 h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: COPPER }}
                  />
                  <p>Private membership access</p>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className="mt-2 h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: COPPER }}
                  />
                  <p>Organized tournaments and card games</p>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className="mt-2 h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: COPPER }}
                  />
                  <p>Comfortable, polished club atmosphere</p>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-zinc-800">
                <p className="text-zinc-500 text-sm">
                  Membership required for participation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Info cards */}
        <section className="mt-12 sm:mt-14">
          <div className="grid md:grid-cols-3 gap-4">
            <InfoCard
              title="Private Membership"
              text="Approved access, better atmosphere, stronger community."
            />

            <InfoCard
              title="Organized Events"
              text="Structured card games and smooth operations."
            />

            <InfoCard
              title="Club Experience"
              text="Comfortable, social, and built for players who appreciate a quality room."
            />
          </div>
        </section>

        {/* Membership steps */}
        <section className="mt-12 sm:mt-14">
          <div className="mb-5">
            <p className="text-sm font-semibold tracking-[0.18em] uppercase text-zinc-500">
              Membership
            </p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-zinc-100">
              How it works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <StepCard
              step="1"
              title="Apply"
              text="Submit your membership application online."
            />

            <StepCard
              step="2"
              title="Get Approved"
              text="Applications are reviewed before approval."
            />

            <StepCard
              step="3"
              title="Start Playing"
              text="Register for tournaments and join club events."
            />
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 sm:mt-14">
          <div className="rounded-3xl border border-zinc-800 bg-zinc-950/75 backdrop-blur p-6 sm:p-8">
            <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <p className="text-sm font-semibold tracking-[0.18em] uppercase text-zinc-500">
                  Ready to Play?
                </p>
                <h2
                  className="mt-2 text-2xl sm:text-3xl font-extrabold"
                  style={{ color: COPPER }}
                >
                  Apply for membership and join River Ranch.
                </h2>
                <p className="mt-3 text-zinc-300 max-w-2xl leading-relaxed">
                  Private members club. Card games and club events
                  in Fayette County, Texas.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link
                  to="/apply"
                  className="px-5 py-3 rounded font-semibold text-zinc-950"
                  style={{ backgroundColor: COPPER }}
                >
                  Apply for Membership
                </Link>

                {isAuthed ? (
                  <Link
                    to="/dashboard"
                    className="px-5 py-3 rounded font-semibold bg-zinc-900/70 backdrop-blur border border-zinc-800 hover:border-zinc-700 text-zinc-100"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="px-5 py-3 rounded font-semibold bg-zinc-900/70 backdrop-blur border border-zinc-800 hover:border-zinc-700 text-zinc-100"
                  >
                    Member Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-10 py-6 border-t border-zinc-900 text-zinc-500 text-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} River Ranch Poker Club</span>
          <span style={{ color: COPPER }}>
            Private Members Club • Fayette County, Texas
          </span>
        </div>
      </div>
    </div>
  );
}